import { useEffect, useState, useSyncExternalStore } from "react";
import type { ModalVariant } from "./Modal";

const BASE_Z_INDEX = 10000;
const Z_INDEX_STEP = 10;

interface StackEntry {
  id: number;
  variant: ModalVariant;
}

let counter = 0;
let stack: readonly StackEntry[] = [];
const listeners = new Set<() => void>();

function setStack(next: readonly StackEntry[]): void {
  stack = next;
  syncBodyAttribute();
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): readonly StackEntry[] {
  return stack;
}

/** Marks the body whenever any center-variant modal is open so the host app can scale itself. */
function syncBodyAttribute(): void {
  if (typeof document === "undefined") {
    return;
  }
  const hasCenter = stack.some((m) => m.variant === "center");
  if (hasCenter) {
    document.body.setAttribute("data-card-modal-open", "");
  } else {
    document.body.removeAttribute("data-card-modal-open");
  }
}

export interface ModalStackEntry {
  zIndex: number;
  isTopmost: boolean;
}

export function useModalStack(
  isOpen: boolean,
  variant: ModalVariant
): ModalStackEntry {
  const [id] = useState(() => ++counter);
  const currentStack = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setStack([...stack, { id, variant }]);
    return () => {
      setStack(stack.filter((m) => m.id !== id));
    };
  }, [isOpen, id, variant]);

  const index = currentStack.findIndex((m) => m.id === id);
  // Sheets float above other modals without pushing them into the recede state,
  // so a center modal is "topmost" as long as no other center modal sits above it.
  const isTopmost =
    index !== -1 &&
    (variant === "sheet"
      ? index === currentStack.length - 1
      : !currentStack.slice(index + 1).some((m) => m.variant === "center"));
  const zIndex = BASE_Z_INDEX + Math.max(0, index) * Z_INDEX_STEP;

  return { zIndex, isTopmost };
}
